import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPatientMonitoringSessions,
  deleteMonitoringSession,
  getPatientGeneralDetails
} from "../../services/patient/details";
import styles from "../../styles/patient-page-modules/MonitoringTime.module.css";
import ReusableTable from "../../components/shared-modules/reuseableTable";
import {
  formatDate,
  formatSecondsToTime,
  generateYears,
  convertUTCToLocal,
  getCurrentLocationId,
  setPatientLocationId
} from "../../utils/helper";
import {
  formatInLocationTZ,
  getLocationYearMonth
} from "../../utils/dateFormatters";
import Reload from "../../components/ui/reload-data";
import Spinner from "../../components/spinner";
import Monitoring from "../../components/shared-modules/monitoring-time-view";
import { Separator } from "../../components/ui/separator";
import AlertDialog from "../../components/alert-dialogue";
import Button from "../../components/button-v1";
import { toast } from "react-hot-toast";
import Table from "../../components/table";
import { FaEdit, FaTrash } from "react-icons/fa";
import EditSession from "./edit-session";
import {
  Select,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue
} from "../../components/ui/select";

// Shared date constants
const MONTHS = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" }
];
const MonitoringTime = () => {
  const patientId = sessionStorage.getItem("_pt_id");
  // Year/month defaults follow the location's business timezone, not the
  // viewer's browser clock — so a dev in PKT and the EST client see the
  // same "current month" relative to the patient's location.
  const { year: currentLocationYear, month: currentLocationMonth } =
    getLocationYearMonth();

  const queryClient = useQueryClient();
  const [queryParams] = useSearchParams();
  const [notes, setNotes] = useState("");
  const [formattedDate, setFormattedDate] = useState("");
  const [time, setTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  // const [totalTime, setTotalTime] = useState("00:00:00");
  const [totalCcmTime, setTotalCcmTime] = useState("00:00:00");
  const [totalRpmTime, setTotalRpmTime] = useState("00:00:00");
  const [monitoringEditTime, setMonitoringEditTime] = useState(null);
  const [year, setYear] = useState(currentLocationYear);
  const [month, setMonth] = useState(currentLocationMonth);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const ccm = sessionStorage.getItem("ccm");
  const rpm = sessionStorage.getItem("rpm");
  const isCcm = ccm === "true" || ccm === true;
  const isRpm = rpm === "true" || rpm === true;

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const patientGeneralDetailsQuery = useQuery(
    ["patient-general-details", patientId],
    () => getPatientGeneralDetails(patientId),
    { enabled: !!patientId, staleTime: 5 * 60 * 1000 }
  );

  // Read/write monitoring sessions under the PATIENT's own location, not the
  // viewer's current location (SCRUM-157). Derived from the patient record so
  // it updates reactively once the record resolves; falls back to the current
  // location until then so the first render still works.
  const locationId =
    patientGeneralDetailsQuery?.data?.location?.id ?? getCurrentLocationId();

  // Mirror it into sessionStorage so the MonitoringCard add-form (which reads
  // getPatientLocationId) saves under the same location.
  useEffect(() => {
    setPatientLocationId(patientGeneralDetailsQuery?.data?.location?.id);
  }, [patientGeneralDetailsQuery?.data?.location?.id]);

  const monitoringSessionQuery = useQuery(
    ["patient-monitoring-sessions", patientId, locationId, year, month],
    () => getPatientMonitoringSessions(patientId, locationId, year, month),
    {
      enabled: !!patientId && !!locationId && !!month && !!year,
      // Always refetch when the user returns to this page so the table
      // reflects the latest server state — important if the viewer's
      // timezone changed (or another session was added on another device)
      // since the cached data was last fetched.
      refetchOnMount: "always"
    }
  );

  const monitoringSessions = monitoringSessionQuery?.data?.results ?? [];

  const [sessions, setSessions] = useState([]);
  const [originalSessions, setOriginalSessions] = useState([]);

  const deleteSessionMutation = useMutation(
    sessionId => deleteMonitoringSession(sessionId, locationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "patient-monitoring-sessions",
          patientId
        ]);
        queryClient.invalidateQueries(["patient-general-details"]);
        toast.success("Session deleted successfully");
      },
      onError: () => {
        toast.error("Could not delete session");
      }
    }
  );

  // const monitoringSessionColumns = [
  //   {
  //     key: "recorded_at",
  //     label: "Date",
  //     alignLeft: true,
  //     render: (reading) => reading.date,
  //   },
  //   {
  //     key: "staff",
  //     label: "Staff",
  //     alignLeft: true,
  //     render: (reading) => reading.staff || "_",
  //   },
  //   {
  //     key: "rpm_duration",
  //     label: "RPM",
  //     alignLeft: true,
  //     render: (reading) => `${reading.rpm_duration}`,
  //   },
  //   {
  //     key: "ccm_duration",
  //     label: "CCM",
  //     alignLeft: true,
  //     render: (reading) => `${reading.ccm_duration}`,
  //   },
  //   {
  //     key: "actions",
  //     label: "Actions",
  //     alignLeft: true,
  //     render: (reading) => (
  //       <section className={styles.action_container}>
  //         <Button
  //           variant="normal"
  //           size="small"
  //           onClick={() => handleEdit(reading.id)}
  //         >
  //           <FaEdit />
  //         </Button>
  //         <Button
  //           variant="normal"
  //           size="small"
  //           onClick={() => handleRemove(reading.id)}
  //         >
  //           <FaTrash />
  //         </Button>
  //       </section>
  //     ),
  //   },
  // ];

  const monitoringSessionColumns = [
    {
      key: "recorded_at",
      label: "Date",
      alignLeft: true,
      render: reading => reading.date
    },
    {
      key: "staff",
      label: "Staff",
      alignLeft: true,
      render: reading => reading.assigned_by_name || "_"
    },
    ...(isRpm
      ? [
          {
            key: "rpm_duration",
            label: "RPM",
            alignLeft: true,
            render: reading => `${reading.rpm_duration}`
          }
        ]
      : []),
    ...(isCcm
      ? [
          {
            key: "ccm_duration",
            label: "CCM",
            alignLeft: true,
            render: reading => `${reading.ccm_duration}`
          }
        ]
      : []),
    {
      key: "actions",
      label: "Actions",
      alignLeft: true,
      render: reading => (
        <section className={styles.action_container}>
          <Button
            variant="normal"
            size="small"
            onClick={() => handleEdit(reading.id)}
          >
            <FaEdit />
          </Button>
          <Button
            variant="normal"
            size="small"
            onClick={() => handleRemove(reading.id)}
          >
            <FaTrash />
          </Button>
        </section>
      )
    }
  ];

  useEffect(() => {
    setTotalRpmTime(
      monitoringSessionQuery.data?.monitoring_time?.formatted_rpm_time
    );
    setTotalCcmTime(
      monitoringSessionQuery.data?.monitoring_time?.formatted_ccm_time
    );
  }, [monitoringSessionQuery.data]);

  useEffect(() => {
    if (monitoringSessions) {
      const formattedSessions = monitoringSessions?.map(session => ({
        id: session.id,
        // date: formatDate(session.created_at),
        date: formatInLocationTZ(session.created_at),
        assigned_by_name: session?.assigned_by_name ?? "_",
        created_at: session.created_at, // Keep original date for filtering
        staff: session?.staff_member?.full_name ?? "_",
        rpm_duration:
          session?.total_rpm_seconds > 0
            ? formatSecondsToTime(session?.total_rpm_seconds)
            : " --",
        ccm_duration:
          session?.total_ccm_seconds > 0
            ? formatSecondsToTime(session?.total_ccm_seconds)
            : " --",
        type:
          session?.total_rpm_seconds > 0
            ? "RPM"
            : session?.total_ccm_seconds > 0
            ? "CCM"
            : ""
      }));
      setOriginalSessions(formattedSessions);
      setSessions(formattedSessions); // <--- This ensures the table gets data immediately
    }
  }, [monitoringSessions]);

  useEffect(() => {
    if (deleteSessionMutation.isSuccess) {
      setIsDeleteDialogOpen(false);
    }
  }, [deleteSessionMutation.isSuccess]);

  const handleDelete = () => {
    deleteSessionMutation.mutate(sessionId);
  };

  // Handle removing a session
  const handleRemove = id => {
    setSessionId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleEdit = id => {
    setSessionId(id);
    setIsEditDialogOpen(true);
  };

  // useEffect(() => {
  //   // Calculate total time spent monitoring

  //   // Exit early if no monitoring sessions data
  //   if (!monitoringSessions || !monitoringSessions.length) {
  //     setTotalRpmTime("00:00:00");
  //     setTotalCcmTime("00:00:00");
  //     return;
  //   }

  //   const totalRpmTimeInSeconds = monitoringSessions.reduce((acc, session) => {
  //     const totalSeconds = session.total_rpm_seconds ?? 0;
  //     return acc + totalSeconds;
  //   }, 0);

  //   const totalCcmTimeInSeconds = monitoringSessions.reduce((acc, session) => {
  //     const totalSeconds = session.total_ccm_seconds ?? 0;
  //     return acc + totalSeconds;
  //   }, 0);

  //   // setTotalTime(formatSecondsToTime(totalTimeInSeconds));
  //   setTotalRpmTime(formatSecondsToTime(totalRpmTimeInSeconds));
  //   setTotalCcmTime(formatSecondsToTime(totalCcmTimeInSeconds));
  // }, [monitoringSessions]);

  const headers = ["Date", "Staff", "Duration"];

  return (
    <div className={styles.patient_page}>
      <div className={styles.monitoring_time_container}>
        <section className={styles.monitoring_time_wrapper}>
          <div className={styles.heading_cont}>
            <h2>Monitoring</h2>
            <Separator />
          </div>
          <Monitoring ccm={ccm} rpm={rpm} />
        </section>

        {/* Monitoring Time Table */}
        <section className={styles.table_content_cont}>
          <div className={styles.table_content_wrapper}>
            <div className={styles.saving_cont}>
              <div className={styles.total_hours}>
                <span>Total Spent Monitoring</span>
                {/* <span>{totalTime}</span> */}
                {isRpm && (
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      width: "100%",
                      marginTop: "5px"
                    }}
                  >
                    <b>RPM: </b> <span>{totalRpmTime}</span>
                  </span>
                )}
                {isCcm && (
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      width: "100%",
                      marginTop: "5px"
                    }}
                  >
                    <b>CCM: </b> <span>{totalCcmTime}</span>
                  </span>
                )}
              </div>
            </div>

            <div className={styles.session_cont}>
              <h2>Sessions</h2>
              <section
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  width: "250px"
                }}
              >
                <Select onChange={value => setMonth(value)} value={month}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select month">
                      {MONTHS.find(m => m.value == month)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {(parseInt(year) < parseInt(currentLocationYear)
                      ? MONTHS
                      : MONTHS.slice(0, parseInt(currentLocationMonth))
                    ).map(m => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select onChange={value => setYear(value)} value={year}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select year">{year}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {generateYears(2024)
                      .reverse()
                      .map(year => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </section>
            </div>

            <div className={styles.table_container}>
              {monitoringSessionQuery?.isLoading ||
              deleteSessionMutation.isLoading ? (
                <section className="content-center">
                  <Spinner />
                </section>
              ) : monitoringSessionQuery?.error ? (
                <Reload
                  onReload={monitoringSessionQuery.refetch}
                  message="Could not load sessions"
                />
              ) : (
                // <ReusableTable
                //   headers={headers}
                //   rows={sessions}
                //   onRowAction={handleRemove}
                //   actionIcon={MdDeleteForever}
                //   showAction={true}
                // />
                <Table
                  columns={monitoringSessionColumns}
                  data={sessions}
                  height="600px"
                />
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Delete Alert Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        setOpen={setIsDeleteDialogOpen}
        title="Delete Session"
        description="Are you sure you want to delete this session?"
      >
        <Button
          variant="normal"
          size="default"
          onClick={() => setIsDeleteDialogOpen(false)}
        >
          Cancel
        </Button>
        <Button
          loading={deleteSessionMutation.isLoading}
          variant="danger"
          size="default"
          onClick={() => handleDelete()}
        >
          Delete
        </Button>
      </AlertDialog>

      {/* Edit Dialog */}
      <EditSession
        isEditDialogOpen={isEditDialogOpen}
        setIsEditDialogOpen={setIsEditDialogOpen}
        sessionId={sessionId}
      />
    </div>
  );
};

export default MonitoringTime;
